import { useAuth0 } from "@auth0/auth0-react";
import Grid from "@material-ui/core/Grid";
import Paper from "@mui/material/Paper";
import { ReactComponent as BackIcon } from "assets/icons/arrow-left.svg";
import { ReactComponent as FilterIcon } from "assets/icons/Filter.svg";
import Loader from "components/loader/Loader";
import MyFilterButton from "components/MyFilterButton";
import { PieChartComponent } from "components/PieChart/PieChartComponent";
import { formatDate } from "helper/dateFormat";
import CommunicationService from "Lib/API/Communication";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import { CampaignDetailsList } from "./CampaignDetailsList";
import {
  Back,
  ChartTitle,
  Details,
  LeftSide,
  MetaLabel,
  RightSide,
  StatusContent,
  StatusDetails,
  StatusWrapper,
  TableDetails,
  TableFilter,
  Title,
  Wrapper,
} from "./Theme";

const getCampaignTypeLabel = (campaignType) => {
  switch (campaignType) {
    case "push":
      return "Push Notification";
    case "whatsapp":
      return "WhatsApp";
    case "sms":
      return "Text Message";
    case "email":
      return "Email";
    default:
      return campaignType || "-";
  }
};

const CampaignDetails = ({ campainDetailsId, setCampainDetailsId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [campaignDetails, setCampaignDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetailsList, setLoadingDetailsList] = useState(false);
  const [campaignDetailsList, setCampaignDetailsList] = useState([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState();


  const isPushCampaign = campaignDetails?.campaignType === "push";

  const sentValue = useMemo(() => campaignDetails?.sent || 0, [campaignDetails?.sent]);
  const otherValueSum = useMemo(
    () =>
      (campaignDetails?.opened || 0) +
      (campaignDetails?.clicked || 0) +
      (campaignDetails?.bounced || 0) +
      (isPushCampaign ? 0 : campaignDetails?.unsubscribed || 0),
    [
      campaignDetails?.opened,
      campaignDetails?.clicked,
      campaignDetails?.bounced,
      campaignDetails?.unsubscribed,
      isPushCampaign,
    ]
  );
  const isAllLessThanSent = useMemo(() => otherValueSum < sentValue, [otherValueSum, sentValue]);

  const getChartData = useCallback(() => {

    const data = [
      ...(isAllLessThanSent ? [
        {
          label: "Unopened",
          value: ((sentValue - otherValueSum) / sentValue) * 100,
          color: "#6432da",
        }
      ] : []),
      ...(campaignDetails?.opened > 0 ? [
        {
        label: "Opened",
        value: ((campaignDetails?.opened / sentValue) * 100).toFixed(0),
        color: "#81DC91",
      }] : []),
      ...(campaignDetails?.unsubscribed > 0 && !isPushCampaign
        ? [
            {
              label: "Unsubscribe",
              value: ((campaignDetails?.unsubscribed / sentValue) * 100).toFixed(2),
              color: "#FA3372",
            },
          ]
        : []),
      ...(campaignDetails?.bounced > 0 ? [{
        label: "Bounced",
        value: ((campaignDetails?.bounced / sentValue) * 100).toFixed(
                2
              ),
        color: "orange",
      }] : []),
      ...(campaignDetails?.clicked > 0 ? [{
        label: "Clicked",
        value: ((campaignDetails?.clicked / sentValue) * 100).toFixed(
                2
              ),
        color: "#feecd5",
      }] : []),
    ]

    return data;
  }, [
    campaignDetails?.sent,
    campaignDetails?.opened,
    campaignDetails?.clicked,
    campaignDetails?.bounced,
    campaignDetails?.unsubscribed,
    isPushCampaign,
    isAllLessThanSent,
    otherValueSum,
    sentValue,
  ]);


  const chartData = getChartData();


  const ViewChart = sentValue > 0;
  const statusInfo = [
    { statusName: "Sent", value: campaignDetails?.sent },
    { statusName: "Opened", value: campaignDetails?.opened },
    { statusName: "Bounced", value: campaignDetails?.bounced },
    ...(isPushCampaign
      ? []
      : [{ statusName: "Unsubscribe", value: campaignDetails?.unsubscribed }]),
    { statusName: "Clicked", value: campaignDetails?.clicked },
  ];
  const audienceFilterItems = [
    { label: "Open", value: "open" },
    { label: "Click", value: "click" },
    { label: "Bounce", value: "bounce" },
    ...(isPushCampaign ? [] : [{ label: "Unsubscribe", value: "unsubscribe" }]),
    { label: "All", value: "all" },
  ];
  const [sortBy, setSortBy] = useState("all");

  const getCampaignDetailByID = async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently();
      const result = await CommunicationService.getCampaignById(
        campainDetailsId,
        token
      );
      setCampaignDetails(result?.result);
    } catch (err) {
      toastr.error("Error", err?.data?.message || "There something went wrong");
    } finally {
      setLoading(false);
    }
  };
  const getCampaignDetailList = async () => {
    setLoadingDetailsList(true);
    const data = {
      campaignId: campainDetailsId,
      filter: {
        status: sortBy,
      },
    };
    try {
      const token = await getAccessTokenSilently();
      const result = await CommunicationService.getCampaignDetailsList(
        token,
        page + 1,
        10,
        data
      );
      setCampaignDetailsList(result?.payload?.data);
      setCount(result?.payload?.pagination?.count);
    } catch (err) {
      toastr.error("Error", err?.data?.message || "There something went wrong");
    } finally {
      setLoadingDetailsList(false);
    }
  };
  useEffect(() => {
    getCampaignDetailByID();
  }, []);
  useEffect(() => {
    getCampaignDetailList();
  }, [sortBy, page]);

  const onSortChange = (value) => {
    setPage(0);
    setSortBy(value);
  }

  return (
    <>
      {loading ? (
        <div
          className="user__loader"
          style={{ height: "320px", maxWidth: "100%" }}
        >
          <Loader />
        </div>
      ) : (
        <Wrapper>
          <Back onClick={() => setCampainDetailsId("")}>
            <BackIcon /> <span>Back To List</span>
          </Back>
          <Title>
            <StatusWrapper status={campaignDetails?.status}>
              {campaignDetails?.status}
            </StatusWrapper>
            <MetaLabel>
              <strong>Campaign name:</strong> {campaignDetails?.campaignName || "-"}
            </MetaLabel>
            <MetaLabel className="campaign-type-meta">
              <strong>Campaign type:</strong>
              <StatusWrapper status="completed">
                {getCampaignTypeLabel(campaignDetails?.campaignType)}
              </StatusWrapper>
            </MetaLabel>
            <MetaLabel>
              <strong>Delivered date:</strong>{" "}
              {formatDate({
                date: campaignDetails?.createdAt,
                formatType: "defaultWithTime",
              })}
            </MetaLabel>
          </Title>
          <Details>
            <LeftSide isZero={!sentValue}>
              <StatusDetails>
                {statusInfo?.map((item) => (
                  <div
                    key={item?.statusName}
                    width={50}
                    style={{
                      height: 100,
                      boxShadow: "0 0 50px 0 rgba(82, 63, 105, 0) !important",
                      border: "1px  solid rgb(0 0 0 / 20%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      paddingLeft: "10px",
                      marginLeft: "25px",
                      marginTop: "15px",
                      width: "15rem",
                    }}
                  >
                    <StatusContent>
                      <p>{item?.statusName}</p>
                      <span>{item?.value}</span>
                    </StatusContent>
                  </div>
                ))}
              </StatusDetails>
              <TableDetails>
                <TableFilter>
                  <span>Audience</span>
                  <MyFilterButton
                    className="campaign_filter"
                    title="All"
                    items={audienceFilterItems}
                    icon={
                      <FilterIcon style={{ width: "19px", height: "17px" }} />
                    }
                    onChange={onSortChange}
                  />
                </TableFilter>

                <CampaignDetailsList
                  rows={campaignDetailsList}
                  loading={loadingDetailsList}
                  setPage={setPage}
                  page={page}
                  count={count}
                  campaignType={campaignDetails?.campaignType}
                />
              </TableDetails>
            </LeftSide>
            {ViewChart && (
              <RightSide>
                <ChartTitle>Campaign Breakdown</ChartTitle>
                <PieChartComponent
                  chartData={chartData}
                  size={{ width: 500, height: 500 }}
                  centerValue={sentValue}
                  centerLabel="Sent"
                />
              </RightSide>
            )}
          </Details>
        </Wrapper>
      )}
    </>
  );
};

export default CampaignDetails;
